<template>
  <div
    class="darwin-number-input"
    :class="`darwin-number-input--size-${size}`"
  >
    <div class="darwin-number-input__container">
      <input
        :id="id"
        ref="inputRef"
        class="darwin-number-input__native-input"
        :value="modelValue"
        type="number"
        :min="min"
        :max="max"
        :disabled="disabled"
        :step="step"
        :aria-label="asString($attrs['aria-label']) || undefined"
        pattern="\d"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <div class="darwin-number-input__button-group">
        <button
          class="darwin-number-input__button darwin-number-input__button--decrement"
          :class="`darwin-number-input__button--size-${size}`"
          :disabled="disabled || (min !== null && modelValue <= min)"
          @click="decrement"
        >
          <Icon name="mono-minus" />
        </button>
        <button
          class="darwin-number-input__button darwin-number-input__button--increment"
          :class="`darwin-number-input__button--size-${size}`"
          :disabled="disabled || (max !== null && modelValue >= max)"
          @click="increment"
        >
          <Icon name="mono-plus" />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent, ref } from 'vue'

import Icon from '@/uiKit/Icon/Icon.vue'

export default defineComponent({
  name: 'NumberInput',
  components: { Icon },
  props: {
    modelValue: { default: NaN, type: Number },
    /**
     * Optional id attribute which is bound to the input element
     */
    id: { default: null, type: String },
    /**
     * Optional size attribute which is bound to the input element
     */
    size: { default: 'sm', type: String as PropType<'xs' | 'sm' | 'md'> },
    /**
     * Optional value for the `min` attribute
     */
    min: { default: -Infinity, type: Number },
    /**
     * Optional value for the `max` attribute
     */
    max: { default: +Infinity, type: Number },
    /**
     * For numeric inputs, optional value for the `step` attribute
     */
    step: { default: 1, type: Number },
    /** How much to increase when + is pressed **/
    incrementBy: { default: 1, type: Number },
    /** How much to decrease when - is pressed **/
    decrementBy: { default: 1, type: Number },
    /**
     * Binds to the 'disabled' html attribute of the input element
     */
    disabled: { default: false, type: Boolean },
  },
  emits: ['update:modelValue', 'focus', 'blur'],
  setup(props, { emit }) {
    const inputRef = ref<HTMLInputElement | null>(null)

    const getValue = (value: number): number => {
      if (props.max !== null && value > props.max) {
        return props.max
      }
      if (props.min !== null && value < props.min) {
        return props.min
      }
      return value
    }

    /**
     * Event handler called when the input element receives focus
     */
    const onFocus = (e: Event): void => {
      emit('focus', e)
    }

    /**
     * Event handler called when the blur element is blurred
     */
    const onBlur = (e: Event): void => {
      emit('blur', e)
    }

    const broadcastValue = (value: number): void => {
      emit('update:modelValue', value)
    }

    /**
     * Event handler called when value changes
     */
    const onInput = (event: Event): void => {
      if (!(event.target instanceof HTMLInputElement)) {
        return
      }
      const value = getValue(Number(event.target.value))
      const maxValue = value >= props.max ? props.max : value
      broadcastValue(maxValue)
      event.target.value = maxValue.toString()
    }

    const increment = (): void => {
      inputRef.value?.stepUp(props.incrementBy)
      broadcastValue(Number(inputRef.value?.value))
    }

    const decrement = (): void => {
      inputRef.value?.stepDown(props.decrementBy)
      broadcastValue(Number(inputRef.value?.value))
    }

    return {
      onInput,
      onFocus,
      onBlur,
      increment,
      decrement,
      inputRef,
      asString: (value: unknown): string => value as string,
    }
  },
})
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/index.scss';

$button-border-radius: 6px;
.darwin-number-input {
  &__container {
    display: flex;
    align-items: center;
    background: var(--color-background-gray-subtle);
    border-radius: 8px;
    height: 100%;
    padding: 0 2px 0 4px;
    gap: 4px;
  }

  &--size-xs {
    height: 24px;
  }

  &--size-sm {
    height: 28px;
  }

  &--size-md {
    height: 32px;
  }

  &__native-input {
    @include typographyRegularBody200;
    color: var(--color-text-default);

    background: transparent;
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    &[type='number'] {
      -moz-appearance: textfield;
    }
    border-radius: 4px;
    flex: 1;
    text-align: center;
    height: 100%;
    min-width: 25px;
    max-width: 50px;
    &:focus {
      background-color: var(--color-background-transparent-pressed);
    }
  }

  &__button-group {
    @include row;
    height: calc(100% - 4px);
    background-color: var(--color-surface-default);
    border-radius: $button-border-radius;

    .darwin-number-input__button {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      padding: 0;

      &--size-xs {
        width: 24px;
      }

      &--size-sm {
        width: 28px;
      }

      &--size-md {
        width: 32px;
      }

      &:first-of-type {
        border-top-left-radius: $button-border-radius;
        border-bottom-left-radius: $button-border-radius;
      }

      &:last-of-type {
        border-top-right-radius: $button-border-radius;
        border-bottom-right-radius: $button-border-radius;
      }

      svg {
        width: 9.5px;
        height: 9.5px;
        color: var(--color-text-subtle);
      }
    }
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-default);
    background: var(--color-background-transparent);
    border: 2px solid transparent;
    position: relative;

    &--size-xs {
      width: 24px;
    }

    &--size-sm {
      width: 28px;
    }

    &--size-md {
      width: 32px;
    }

    &:not(:disabled):hover {
      background-color: var(--color-background-transparent-hovered);
    }

    &:not(:disabled):active {
      background-color: transparent;
      border: 2px solid var(--color-border-focused);
      z-index: 1;
    }

    &:disabled {
      color: var(--color-text-disabled);
      border-color: $colorInteractiveOutlineDisabled;
      background-color: $colorInteractiveOutlineDisabled;
      cursor: not-allowed;
    }
  }
}
</style>
